/*
 * a11y-fixes.js — keyboard & screen-reader fixes (WCAG 2.2 AA audit, Oct 2026).
 * Everything here is behavioural / ARIA only: nothing changes for mouse users.
 * To disable all of it, remove the <script> tag for this file at the bottom of layout/theme.liquid.
 */
(function () {
  'use strict';
  var d = document;
  var html = d.documentElement;

  /* ------------------------------------------------------------------
   * 0. Keyboard-modality flag (used by CSS so text-field focus rings
   *    only appear for keyboard users, never on mouse click).
   * ------------------------------------------------------------------ */
  d.addEventListener('keydown', function (e) {
    if (e.key === 'Tab') html.classList.add('a11y-kbd');
  }, true);
  ['mousedown', 'touchstart'].forEach(function (type) {
    d.addEventListener(type, function () { html.classList.remove('a11y-kbd'); }, { capture: true, passive: true });
  });

  /* Polite live region for status messages (token builder qty / rewards). */
  var liveEl;
  function announce(msg) {
    if (!msg) return;
    if (!liveEl) {
      liveEl = d.createElement('div');
      liveEl.className = 'visually-hidden';
      liveEl.setAttribute('role', 'status');
      liveEl.setAttribute('aria-live', 'polite');
      d.body.appendChild(liveEl);
    }
    liveEl.textContent = '';
    setTimeout(function () { liveEl.textContent = msg; }, 60);
  }

  function isVisible(el) {
    if (!el) return false;
    if (!(el.offsetWidth || el.offsetHeight || el.getClientRects().length)) return false;
    var cs = getComputedStyle(el);
    return cs.visibility !== 'hidden';
  }

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), summary';
  function focusables(root) {
    return Array.prototype.filter.call(root.querySelectorAll(FOCUSABLE), isVisible);
  }
  function trapTab(e, root) {
    if (e.key !== 'Tab') return;
    var list = focusables(root);
    if (!list.length) return;
    var first = list[0], last = list[list.length - 1];
    if (e.shiftKey && (d.activeElement === first || !root.contains(d.activeElement))) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && (d.activeElement === last || !root.contains(d.activeElement))) {
      e.preventDefault(); first.focus();
    }
  }

  /* ------------------------------------------------------------------
   * 1. Generic: Enter / Space activate non-<button> controls that we
   *    expose as role="button" (divs/spans/links used as buttons).
   * ------------------------------------------------------------------ */
  d.addEventListener('keydown', function (e) {
    var t = e.target;
    if (!t || !t.matches) return;
    if (!t.matches('[data-a11y-btn], [role="button"]:not(button):not(input):not(summary)')) return;
    var isEnter = e.key === 'Enter';
    var isSpace = e.key === ' ' || e.key === 'Spacebar';
    if (!isEnter && !isSpace) return;
    if (isEnter && t.tagName === 'A' && t.hasAttribute('href')) return; // native link already clicks on Enter
    if (t.getAttribute('aria-disabled') === 'true') { e.preventDefault(); return; }
    e.preventDefault();
    t.click();
  });

  function makeButton(el, label) {
    if (!el || el.__a11yBtn) return;
    el.__a11yBtn = true;
    var tag = el.tagName;
    if (tag !== 'BUTTON' && !el.hasAttribute('role')) el.setAttribute('role', 'button');
    if (tag !== 'BUTTON' && !(tag === 'A' && el.hasAttribute('href')) && !el.hasAttribute('tabindex')) el.setAttribute('tabindex', '0');
    if (tag !== 'BUTTON') el.setAttribute('data-a11y-btn', '');
    if (label && !el.getAttribute('aria-label')) el.setAttribute('aria-label', label);
  }
  function txt(el) { return el ? (el.textContent || '').replace(/\s+/g, ' ').trim() : ''; }

  /* ------------------------------------------------------------------
   * 2. Enhancements that must be re-applied when apps / AJAX re-render
   *    markup (cart drawer, recommendations, FAQ variants, iframes).
   * ------------------------------------------------------------------ */
  function enhance() {
    // Cart drawer "Complete the Collection" add buttons (div) + gift packaging info icon
    d.querySelectorAll('.product-recommendations--item__button').forEach(function (b) {
      var item = b.closest('.product-recommendations--item') || b.parentElement;
      var title = item ? txt(item.querySelector('.product-recommendations--item__title')) : '';
      title = title.replace(/\s*$/, '');
      var price = txt(b.querySelector('.item__price'));
      makeButton(b, 'Add ' + (title || 'item') + (price ? ', ' + price : '') + ' to cart');
    });
    d.querySelectorAll('a.Upsell--Popup__Link').forEach(function (a) {
      if (!txt(a) && !a.getAttribute('aria-label')) a.setAttribute('aria-label', 'About gift packaging');
      var svg = a.querySelector('svg'); if (svg) svg.setAttribute('aria-hidden', 'true');
    });
    // Personalise / monogram / date "links" in the cart drawer (re-rendered by AJAX)
    d.querySelectorAll('.cart-item--product-update').forEach(function (el) { makeButton(el); });
    // Other FAQ variants that are still <div> (landing pages, gifting page)
    d.querySelectorAll('[data-faq--question]').forEach(function (q) {
      if (q.tagName !== 'BUTTON') makeButton(q);
      var item = q.closest('[data-faq--item]');
      if (item) q.setAttribute('aria-expanded', item.classList.contains('active') ? 'true' : 'false');
    });
    // Mega-menu "Customizable" category toggles
    d.querySelectorAll('[data-mega-menu-category] .category__title').forEach(function (t) {
      makeButton(t);
      var cat = t.closest('[data-mega-menu-category]');
      t.setAttribute('aria-expanded', cat && cat.classList.contains('active') ? 'true' : 'false');
    });
    // Mobile-menu sub-page dropdowns
    d.querySelectorAll('.menu-item__link-list__dropdown .dropdown__item, .menu-item__dropdown').forEach(function (el) {
      if (!el.querySelector('a[href]') || el.classList.contains('dropdown__item')) makeButton(el);
    });
    // Token list "Personalize the Back?" links and search clear icons
    d.querySelectorAll('.tokensList .personalizeBack').forEach(function (el) {
      if (el.tagName !== 'BUTTON' && !(el.tagName === 'A' && el.hasAttribute('href'))) {
        var v = el.closest('.tokenVariant');
        makeButton(el, v ? 'Personalize the back of ' + txt(v.querySelector('.tokenTitle')) + ' token' : null);
      }
    });
    d.querySelectorAll('.custom-search-container .Close__Icon').forEach(function (el) { makeButton(el, 'Clear search'); });
    // "Notify when available" links (href="#") behave as buttons
    d.querySelectorAll('a.swym-add-to-watchlist-view-product[href="#"]').forEach(function (a) {
      if (!a.hasAttribute('role')) a.setAttribute('role', 'button');
    });
    // Token builder +/- divs: reflect disabled state
    d.querySelectorAll('.ProductCheck .button[role="button"]').forEach(function (b) {
      b.setAttribute('aria-disabled', b.classList.contains('disabled') ? 'true' : 'false');
    });
    // iframes without a title (pixels, affiliate / SMS widgets)
    d.querySelectorAll('iframe:not([title])').forEach(function (f) {
      var src = f.getAttribute('src') || '';
      var t = 'Third-party content';
      if (/web-pixel/.test(f.id || src)) t = 'Analytics (no visible content)';
      else if (/snowball/i.test(src)) t = 'Social Snowball affiliate widget';
      else if (/postscript/i.test(src)) t = 'Postscript SMS sign-up';
      else if (/omnisend/i.test(src)) t = 'Omnisend sign-up form';
      else { try { t = 'Third-party content (' + new URL(src, location.href).hostname + ')'; } catch (e) {} }
      f.setAttribute('title', t);
    });
    // Cart icon label stays in sync with the AJAX-updated count
    var cartBtn = d.getElementById('cart-icon-bubble');
    if (cartBtn) {
      var countEl = cartBtn.querySelector('.cart-count-bubble span:last-child');
      var n = countEl ? parseInt(txt(countEl), 10) || 0 : 0;
      var label = 'Cart (' + n + (n === 1 ? ' item)' : ' items)');
      if (cartBtn.getAttribute('aria-label') !== label) cartBtn.setAttribute('aria-label', label);
    }
  }
  var enhanceTimer;
  function scheduleEnhance() {
    clearTimeout(enhanceTimer);
    enhanceTimer = setTimeout(enhance, 120);
  }

  /* FAQ: keep aria-expanded in sync after the theme's own click handler runs. */
  d.addEventListener('click', function (e) {
    if (e.target.closest && (e.target.closest('[data-faq--question]') || e.target.closest('[data-mega-menu-category]'))) {
      setTimeout(enhance, 0);
      setTimeout(enhance, 450);
    }
  });

  /* ------------------------------------------------------------------
   * 3. Mobile menu drawer (#sidebar-menu): aria-expanded, inert when
   *    closed, focus moves in on open, Tab is trapped, Escape closes,
   *    focus returns to the menu button.
   * ------------------------------------------------------------------ */
  function initSidebar() {
    var sb = d.getElementById('sidebar-menu');
    if (!sb) return;
    var opener = d.querySelector('button[data-action="open-drawer-custom"][data-drawer-id="sidebar-menu"]');
    var closer = sb.querySelector('[data-action="close-drawer-custom"]');
    var wasOpen = false, lastSubLink = null;
    function isOpen() { return sb.getAttribute('aria-hidden') === 'false'; }
    function sync() {
      var open = isOpen();
      if (opener) opener.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (closer) closer.setAttribute('aria-expanded', open ? 'true' : 'false');
      if ('inert' in sb) sb.inert = !open;
      if (open && !wasOpen) {
        setTimeout(function () { (closer || focusables(sb)[0] || sb).focus(); }, 60);
      } else if (!open && wasOpen) {
        if (sb.contains(d.activeElement) || d.activeElement === d.body) {
          if (opener && isVisible(opener)) opener.focus();
        }
      }
      wasOpen = open;
    }
    new MutationObserver(sync).observe(sb, { attributes: true, attributeFilter: ['aria-hidden'] });
    sync();
    d.addEventListener('keydown', function (e) {
      if (!isOpen()) return;
      if (e.key === 'Escape' || e.key === 'Esc') {
        e.preventDefault();
        if (closer) closer.click(); else sb.setAttribute('aria-hidden', 'true');
        return;
      }
      trapTab(e, sb);
    });
    // Sub-pages (Tokens / Token Holders / Travel Resources): move focus to "Back" and back again
    sb.addEventListener('click', function (e) {
      var link = e.target.closest('.menu-item .item__link');
      var back = e.target.closest('.menu-back-button');
      if (link) {
        lastSubLink = link;
        setTimeout(function () {
          var b = sb.querySelector('.menu-back-button');
          if (b && isVisible(b) && html.classList.contains('a11y-kbd')) b.focus();
        }, 60);
      } else if (back && lastSubLink) {
        var l = lastSubLink;
        setTimeout(function () { if (isVisible(l)) l.focus(); }, 60);
      }
    });
  }

  /* ------------------------------------------------------------------
   * 4. Desktop mega menu / dropdowns: open on keyboard focus, close on
   *    focus-out or Escape, expose aria-expanded. Hover is unchanged.
   * ------------------------------------------------------------------ */
  function initMegaMenu() {
    var items = d.querySelectorAll('.Header__MainNav.Nav__First > ul > li[aria-haspopup="true"]');
    Array.prototype.forEach.call(items, function (li, i) {
      var top = li.querySelector(':scope > a');
      var panel = li.querySelector(':scope > .mega--menu, :scope > .DropdownMenu');
      if (!top || !panel) return;
      if (!panel.id) panel.id = 'a11y-nav-panel-' + i;
      top.setAttribute('aria-expanded', 'false');
      top.setAttribute('aria-controls', panel.id);
      if (!panel.hasAttribute('aria-hidden')) panel.setAttribute('aria-hidden', 'true');
      var suppress = false;
      function show() { panel.setAttribute('aria-hidden', 'false'); top.setAttribute('aria-expanded', 'true'); }
      function hide() { panel.setAttribute('aria-hidden', 'true'); top.setAttribute('aria-expanded', 'false'); }
      li.addEventListener('focusin', function () {
        if (!suppress && html.classList.contains('a11y-kbd')) show();
      });
      li.addEventListener('focusout', function (e) {
        if (!li.contains(e.relatedTarget)) { suppress = false; panel.inert = false; if (!li.matches(':hover')) hide(); }
      });
      li.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' || e.key === 'Esc') {
          if (panel.getAttribute('aria-hidden') === 'false') {
            e.preventDefault(); suppress = true; hide();
            panel.inert = true; // Tab now skips the closed panel; cleared again on focus-out / hover
            top.focus();
          }
        }
      });
      li.addEventListener('mouseenter', function () { panel.inert = false; top.setAttribute('aria-expanded', 'true'); if (panel.classList.contains('DropdownMenu')) panel.setAttribute('aria-hidden', 'false'); });
      li.addEventListener('mouseleave', function () { if (!li.contains(d.activeElement)) hide(); });
    });
  }

  /* ------------------------------------------------------------------
   * 5. Cart drawer: move focus in on open, trap Tab, return focus on close.
   * ------------------------------------------------------------------ */
  function patchCartDrawer() {
    var C = window.customElements && customElements.get('cart-drawer');
    if (!C || C.prototype.__a11yPatched) return !!C;
    C.prototype.__a11yPatched = true;
    var origOpen = C.prototype.open, origClose = C.prototype.close;
    C.prototype.open = function (triggeredBy) {
      var self = this;
      var wasActive = self.classList.contains('active');
      if (!wasActive && !triggeredBy && d.activeElement && d.activeElement !== d.body && !self.contains(d.activeElement)) {
        self.activeElement = d.activeElement;
      }
      var r = origOpen.apply(this, arguments);
      setTimeout(function () {
        if (!self.classList.contains('active')) return;
        var inner = self.querySelector('.drawer__inner');
        if (!inner) return;
        if (!self.__a11yKeydown) {
          self.__a11yKeydown = function (e) { if (self.classList.contains('active')) trapTab(e, inner); };
          d.addEventListener('keydown', self.__a11yKeydown);
        }
        if (!self.contains(d.activeElement) || d.activeElement === d.body) {
          var target = wasActive ? inner : (self.querySelector('.drawer__close') || inner);
          target.focus({ preventScroll: true });
        }
      }, 350);
      return r;
    };
    C.prototype.close = function () {
      var r = origClose.apply(this, arguments);
      var back = this.activeElement;
      if (!back || !d.contains(back) || !isVisible(back)) back = d.getElementById('cart-icon-bubble');
      if (back && (this.contains(d.activeElement) || d.activeElement === d.body)) {
        try { back.focus({ preventScroll: true }); } catch (e) {}
      }
      return r;
    };
    return true;
  }

  /* ------------------------------------------------------------------
   * 6. Customer account drawer: same focus handling via its events.
   * ------------------------------------------------------------------ */
  function initAccountDrawer() {
    var opener = null, keyHandler = null;
    d.addEventListener('customer-drawer:opened', function () {
      var host = d.querySelector('customer-account-drawer');
      if (!host) return;
      var panel = host.querySelector('.is-open') || host;
      opener = (d.activeElement && d.activeElement !== d.body) ? d.activeElement : d.querySelector('.Account__Icon');
      setTimeout(function () {
        var list = focusables(panel);
        if (list[0]) list[0].focus();
      }, 350);
      keyHandler = function (e) {
        if (e.key === 'Escape' || e.key === 'Esc') { if (host.close) host.close(); return; }
        trapTab(e, panel);
      };
      d.addEventListener('keydown', keyHandler);
    });
    d.addEventListener('customer-drawer:closed', function () {
      if (keyHandler) d.removeEventListener('keydown', keyHandler);
      keyHandler = null;
      if (opener && d.contains(opener) && isVisible(opener)) opener.focus();
    });
  }

  /* ------------------------------------------------------------------
   * 7. Token builder: keep focus in place after ADD / remove, and
   *    announce quantity + rewards message to screen readers.
   * ------------------------------------------------------------------ */
  function rewardsMessage() {
    var msgs = d.querySelectorAll('.cart--rewards__numOuter, .cart--rewards__success');
    for (var i = 0; i < msgs.length; i++) { if (isVisible(msgs[i])) return txt(msgs[i]); }
    return '';
  }
  d.addEventListener('click', function (e) {
    var t = e.target.closest && e.target.closest('[data-card-add], [data-card-plus], [data-card-minus], [data-card-delete], .ProductCheck .button');
    if (!t) return;
    var card = t.closest('.tokenCard, .tokenVariant');
    if (!card) return;
    var title = txt(card.querySelector('.tokenTitle'));
    setTimeout(function () {
      // Focus recovery: the clicked control may have been hidden (ADD -> qty controls, qty 0 -> ADD)
      var ae = d.activeElement;
      if (!ae || ae === d.body || !isVisible(ae) || !card.contains(ae)) {
        var next = null;
        if (t.matches('[data-card-add]')) next = card.querySelector('[data-card-plus]');
        else if (t.matches('[data-card-delete]')) next = card.querySelector('[data-card-personalize]');
        if (!next || !isVisible(next)) next = card.querySelector('[data-card-add]');
        if (!next || !isVisible(next)) next = focusables(card)[0];
        if (next && isVisible(next) && (html.classList.contains('a11y-kbd') || ae === d.body)) next.focus({ preventScroll: true });
      }
      enhance();
    }, 80);
    setTimeout(function () {
      var qtyEl = card.querySelector('[data-card-count]');
      var qty = qtyEl && isVisible(qtyEl) ? txt(qtyEl) : '';
      if (!qty) { var inp = card.querySelector('input.quantity'); qty = inp ? inp.value : ''; }
      if (t.matches('[data-card-delete]')) { announce('Engraving removed for ' + title + '.'); return; }
      var msg = title + ': ' + (qty || '0') + ' selected.';
      var r = rewardsMessage();
      announce(r ? msg + ' ' + r : msg);
    }, 450);
  });

  /* ------------------------------------------------------------------
   * 8. Scrolling trust ticker: pause / play button (if enabled in
   *    Theme settings > Accessibility) and reduced-motion support.
   * ------------------------------------------------------------------ */
  function initTicker() {
    d.querySelectorAll('[data-a11y-ticker-toggle]').forEach(function (btn) {
      var wrap = btn.closest('.running-line-wrapper');
      var line = wrap && wrap.querySelector('.running-line');
      if (!line) return;
      btn.addEventListener('click', function () {
        var paused = !wrap.classList.contains('is-a11y-paused');
        wrap.classList.toggle('is-a11y-paused', paused);
        line.style.animationPlayState = paused ? 'paused' : 'running';
        btn.setAttribute('aria-pressed', paused ? 'true' : 'false');
        btn.setAttribute('aria-label', paused ? 'Play scrolling highlights' : 'Pause scrolling highlights');
      });
    });
    // Keyboard users: pause while focus is inside the ticker (mirrors the existing hover pause)
    d.querySelectorAll('.running-line').forEach(function (line) {
      line.addEventListener('focusin', function () { line.classList.add('a11y-focus-paused'); });
      line.addEventListener('focusout', function () { line.classList.remove('a11y-focus-paused'); });
    });
  }

  /* ------------------------------------------------------------------ */
  function init() {
    enhance();
    initSidebar();
    initMegaMenu();
    initAccountDrawer();
    initTicker();
    if (!patchCartDrawer() && window.customElements) {
      customElements.whenDefined('cart-drawer').then(patchCartDrawer);
    }
    new MutationObserver(scheduleEnhance).observe(d.body, { childList: true, subtree: true });
    window.addEventListener('cart:updated', scheduleEnhance);
  }
  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', init); else init();
})();
